import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const AUTH_IMG = "https://images.unsplash.com/photo-1616750819456-5cdee9b85d22";

const AuthPage = ({ mode }) => {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const loggedUser = isLogin
        ? await login(form.email, form.password)
        : await register(form.name, form.email, form.password);
      navigate(loggedUser.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
      <div className="hidden lg:block relative">
        <img src={AUTH_IMG} alt="Kaós" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="flex flex-col justify-center px-6 md:px-16 lg:px-24 py-20">
        <div className="max-w-sm w-full mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-champagne mb-5">KAÓS®</p>
          <h1 className="font-serif text-4xl tracking-tight text-charcoal">
            {isLogin ? t("auth.loginTitle") : t("auth.registerTitle")}
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-6">
            {!isLogin && (
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("auth.name")}</label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="auth-name"
                  className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal"
                />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("auth.email")}</label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="auth-email"
                className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("auth.password")}</label>
              <Input
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="auth-password"
                className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal"
              />
            </div>

            {isLogin && <Link to="/forgot-password" className="block text-right text-xs text-stone-500 underline">Password dimenticata?</Link>}

            {error && <p className="text-sm text-destructive" data-testid="auth-error">{error}</p>}

            <Button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full rounded-none bg-charcoal text-white hover:bg-stone-800 h-12 text-xs uppercase tracking-[0.2em]"
            >
              {loading ? "..." : isLogin ? t("auth.loginCta") : t("auth.registerCta")}
            </Button>
          </form>

          <Link
            to={isLogin ? "/register" : "/login"}
            data-testid="auth-switch"
            className="block text-center text-sm text-stone-500 hover:text-charcoal transition-colors mt-8"
          >
            {isLogin ? t("auth.toRegister") : t("auth.toLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
